<template>
  <div class="connexion">
    <div class="login-card">
      <div class="form-title" style="text-align: center;">Connexion</div>

      <form @submit.prevent="submitForm" class="form">
        <div v-if="errorMessage" class="error" style="text-align: center; margin-top: 10px;">
          {{ errorMessage }}
        </div>

        <div class="form-group">
          <label for="email" class="form-label">Mon Adresse Email</label>
          <input
            type="email"
            id="email"
            v-model="email"
            @input="validateEmail"
            class="form-input"
            required
          />
          <div v-if="emailError" class="error">{{ emailError }}</div>
        </div>

        <div class="form-group">
          <label for="password" class="form-label">Mon Mot de passe</label>
          <input
            type="password"
            id="password"
            v-model="password"
            class="form-input"
            required
          />
          <div v-if="passwordError" class="error">{{ passwordError }}</div>
        </div>

        <div style="display: flex; justify-content: center;" class="form-group">
          <button type="submit" class="btn btn-lg">Me connecter</button>
        </div>

        <div class="forget-password">
          <a href="#" @click="openResetPasswordModal">J'ai oublié mon mot de passe</a>
        </div>

        <div class="forget-password">
          <a href="#" @click="noAccount">Créer un compte</a>
        </div>
      </form>

      <div v-if="successMessage" class="success" :style="{ backgroundColor: successColor }">
        {{ successMessage }}
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="forgetPassword" class="modal">
      <div class="modal-content">
        <h2>Mot de passe oublié ?</h2>

        <form @submit.prevent="submitForgetPassword">
          <div class="form-group">
            <label for="change-email" class="form-label">
              Veuillez entrer votre adresse email
            </label>

            <input
              type="email"
              id="forget-email"
              v-model="resetEmail"
              @input="validateResetEmail"
              class="form-input"
              required
            />

            <div v-if="resetEmailError" class="error">
              {{ resetEmailError }}
            </div>
          </div>

          <div
            v-if="resetStatus == 'success'"
            class="success form-group"
            :style="{ backgroundColor: 'green' }"
          >
            Un email de réinitialisation de mot de passe a été envoyé à {{ resetEmail }}.
          </div>

          <div style="display: flex; justify-content: flex-end;" class="form-group">
            <button
              @click="closeResetPasswordModal"
              class="btn btn-lg"
              :disabled="resetStatus == 'loading'"
            >
              <span v-if="resetStatus == 'success'">Fermer</span>
              <span v-else>Annuler</span>
            </button>

            <button
              type="submit"
              class="btn btn-lg"
              style="margin-left: 10px;"
              :disabled="resetStatus == 'loading' || resetEmailError != ''"
            >
              <Loader v-if="resetStatus == 'loading'" />
              <span v-else-if="resetStatus == 'success' || resetStatus == 'error'">Renvoyer</span>
              <span v-else>Envoyer</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { userState } from "@/utils/store";
import { useRouter, useRoute } from "vue-router/composables";
import Loader from "@/components/Loader.vue";

export default {
  components: {
    Loader,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Rediriger si déjà connecté
    if (userState.isLoggedIn()) {
      const redirect = route.query?.redirect ? String(route.query.redirect) : "/mes-menus";
      router.replace(redirect).catch(() => {});
    }

    const email = ref("");
    const resetEmail = ref("");
    const password = ref("");
    const emailError = ref("");
    const resetEmailError = ref("");
    const passwordError = ref("");
    const errorMessage = ref("");
    const successMessage = ref("");
    const resetStatus = ref("");
    const successColor = ref("");
    const forgetPassword = ref(false);

    const validateEmail = () => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
      emailError.value = re.test(email.value) ? "" : "Adresse email non valide";
    };

    const validateResetEmail = () => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
      resetEmailError.value = re.test(resetEmail.value) ? "" : "Adresse email non valide";
    };

    const submitForm = async () => {
      if (emailError.value || passwordError.value) {
        throw new Error("Formulaire invalide");
      }

      errorMessage.value = "";

      try {
        await userState.login(email.value, password.value);
        clearForm();

        // ✅ Si on vient d'une page protégée, on redirige vers cette page
        const redirect = route.query && route.query.redirect ? String(route.query.redirect) : "";
        if (redirect) {
          router.push(redirect).catch(() => {});
        } else {
          router.push("/mes-menus").catch(() => {});
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          errorMessage.value = error.response.data.detail || "Email ou mot de passe invalide";
        } else {
          errorMessage.value = "Une erreur est survenue, veuillez réessayer.";
        }
      }
    };

    const submitForgetPassword = async () => {
      if (resetEmailError.value) {
        return;
      }

      resetStatus.value = "loading";
      userState
        .resetPassword(resetEmail.value)
        .then(() => {
          resetStatus.value = "success";
        })
        .catch((error) => {
          resetStatus.value = "error";
          throw error;
        });
    };

    const clearForm = () => {
      email.value = "";
      password.value = "";
    };

    const closeResetPasswordModal = () => {
      forgetPassword.value = false;
    };

    const noAccount = () => {
      router.push("/inscription").catch(() => {});
    };

    const openResetPasswordModal = () => {
      resetEmail.value = "";
      resetStatus.value = "";
      forgetPassword.value = true;
    };

    return {
      userState,
      email,
      emailError,
      resetEmail,
      password,
      passwordError,
      errorMessage,
      resetEmailError,
      successMessage,
      successColor,
      forgetPassword,
      openResetPasswordModal,
      resetStatus,
      closeResetPasswordModal,
      noAccount,
      validateEmail,
      validateResetEmail,
      submitForm,
      submitForgetPassword,
      Loader,
    };
  },
};
</script>

<style scoped>
.connexion {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-card {
  max-width: 500px;
  width: 100%;

  font-family: "TangoSans", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #436f0e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: 0.3s;
  padding: 16px;
  background-color: #b5d088;
  border-radius: 5px;
  font-size: 18px;
}

.form {
  padding: 20px;
  border-radius: 5px;
}

.btn {
  background-color: rgb(0, 76, 64);
  color: white;
  border: 1px solid rgb(0, 76, 64);
  transition: 0.2s ease-in-out;
  border: none;
}

.btn:hover {
  background-color: rgb(161, 201, 0);
  color: white;
  border: none;
  transition: 0.2s ease-in-out;
}

.error,
.success {
  padding: 10px;
  border-radius: 5px;
}

.error {
  background-color: #f44336;
  color: white;
}

.success {
  color: white;
}

.close {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}

.forget-password {
  margin-top: 10px;
  text-align: center;
}

.forget-password a {
  color: #007bff;
  text-decoration: none;
}

.forget-password a:hover {
  text-decoration: underline;
}
</style>
